import { useMemo } from 'react';
import { tracking } from '@iwoca/frontend-tracking-library';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { driver, DriveStep } from 'driver.js';
import { useNavigate } from 'react-router-dom';
import { getAmplitudeDefaultEventProperties } from 'utils/amplitudeEvents';
import { PATH_NAMES } from 'utils/routes/pathNames';
import {
  markTutorialAsComplete,
  useGetIntroducerTutorials,
} from './useTutorials.api';
import {
  addRelaunchStepWhenApplicable,
  closeTourOrJumpToFinalStep,
} from './useTutorials.helpers';
import { TTutorialName } from './useTutorials.types';

const trackClick = (tourName: string, buttonText: string) => {
  tracking.ampli.ctaClick({
    ...getAmplitudeDefaultEventProperties(),
    'CTA text': buttonText,
    category: tourName,
  });
};

export const useTutorials = (autoTriggered: boolean = true) => {
  const navigate = useNavigate();
  const { introducerId } = useIntroducerContext();
  const { data: tutorialsData } = useGetIntroducerTutorials(autoTriggered);

  const triggerNavigationTour = () => {
    const handleClose = async () => {
      await closeTourOrJumpToFinalStep({
        tour: navigationTour,
        autoTriggered,
        onTourComplete: async () => {
          if (!tutorialCompletion['new_user']) {
            await markTutorialAsComplete({
              introducerId,
              tutorial: 'new_user',
            });
          }
        },
      });
    };

    const onCompleteTour = async () => {
      await handleClose();
      trackClick('Navigation', 'Got it');
    };

    const onNextClick = () => {
      navigationTour.moveNext();
      trackClick('Navigation', 'Next');
    };

    const PROGRESS_STEP_COUNT = 2;
    const steps: DriveStep[] = [
      {
        element: '#nav-ctas-left',
        popover: {
          showProgress: true,
          progressText: `1 of ${PROGRESS_STEP_COUNT}`,
          title: '🚀 Welcome to the Portal',
          description: `<p>You can always access the <strong>Dashboard</strong> to manage applications, explore <strong>Products</strong> we offer, and use the <strong>Calculator</strong> for quick estimates.</p><p>Click <strong>“Create a new app”</strong> anytime to start a new application.</p>`,
          side: 'bottom',
          align: 'center',
          nextBtnText: 'Next',
          onNextClick,
        },
        disableActiveInteraction: true,
      },
      {
        element: '#nav-ctas-right',
        popover: {
          showProgress: true,
          progressText: `2 of ${PROGRESS_STEP_COUNT}`,
          title: '💡 Helpful tools and support',
          description: `<p>Stay up to date with <strong>What's New</strong>, find quick answers in <strong>FAQs</strong>, or click your profile icon to invite colleagues and relaunch tours.</p><p>These tools are here to support you as you create applications.</p>`,
          nextBtnText: 'Got it',
          side: 'bottom',
          align: 'center',
          onNextClick: autoTriggered ? onNextClick : onCompleteTour,
        },
        disableActiveInteraction: true,
      },
    ];

    addRelaunchStepWhenApplicable(steps, autoTriggered, onCompleteTour);

    const navigationTour = driver({
      showButtons: ['next', 'close'],
      popoverClass: 'driver-popover-custom',
      steps,
      onCloseClick: handleClose,
      overlayClickBehavior: undefined,
    });

    navigationTour.drive();
  };

  const triggerCalculatorTour = () => {
    const onNextClick = () => {
      calculatorTour.moveNext();
      trackClick('Calculator', 'Next');
    };

    const handleClose = async () => {
      await closeTourOrJumpToFinalStep({
        tour: calculatorTour,
        autoTriggered,
        onTourComplete: async () => {
          if (!tutorialCompletion['calculator']) {
            await markTutorialAsComplete({
              introducerId,
              tutorial: 'calculator',
            });
          }
        },
      });
    };

    const onCompleteTour = async () => {
      await handleClose();
      trackClick('Calculator', 'Got it');
      navigate(PATH_NAMES.calculator(), { replace: true });
    };

    const PROGRESS_STEP_COUNT = 4;

    const steps: DriveStep[] = [
      {
        element: '#term-lengths',
        popover: {
          showProgress: true,
          progressText: `1 of ${PROGRESS_STEP_COUNT}`,
          title: '⏳ Choose term length',
          description: `<p>Try different term lengths to see how they affect monthly and total repayments.</p>`,
          side: 'bottom',
          align: 'center',
          nextBtnText: 'Next',
          onNextClick,
        },
        disableActiveInteraction: true,
      },
      {
        element: '#calculator-customisation',
        popover: {
          showProgress: true,
          progressText: `2 of ${PROGRESS_STEP_COUNT}`,
          popoverClass: 'driver-popover-custom right-aligned',
          title: '💵 Customise your loan',
          description: `<p>Enter loan amount, interest rate, and withdrawal fee - or use the sliders to test different scenarios.</p>`,
          nextBtnText: 'Next',
          side: 'right',
          align: 'center',
          onNextClick,
        },
        disableActiveInteraction: true,
      },
      {
        element: '#repayment-schedule',
        popover: {
          showProgress: true,
          progressText: `3 of ${PROGRESS_STEP_COUNT}`,
          popoverClass: 'driver-popover-custom left-aligned',
          title: '📅 View repayment schedule',
          description: `<p>The repayment schedule updates automatically based on your chosen term length and loan details.</p><p>You can <strong>export it as a CSV file</strong> to share with clients.</p>`,
          nextBtnText: 'Next',
          side: 'left',
          align: 'center',
          onNextClick,
        },
        disableActiveInteraction: true,
      },
      {
        element: '#overpayment-options',
        popover: {
          showProgress: true,
          progressText: `4 of ${PROGRESS_STEP_COUNT}`,
          popoverClass: 'driver-popover-custom right-aligned',
          title: '💡 Calculate early repayments',
          description: `<p>Tick <strong>“Calculate early repayments”</strong> to estimate repayments based on a set timeframe or amount.</p><p>The repayment schedule will update automatically.</p>`,
          nextBtnText: 'Got it',
          side: 'right',
          align: 'center',
          onNextClick: autoTriggered ? onNextClick : onCompleteTour,
        },
        disableActiveInteraction: true,
      },
    ];

    addRelaunchStepWhenApplicable(steps, autoTriggered, onCompleteTour);

    const calculatorTour = driver({
      showButtons: ['next', 'close'],
      popoverClass: 'driver-popover-custom',
      steps,
      onCloseClick: handleClose,
      overlayClickBehavior: undefined,
    });

    calculatorTour.drive();
  };

  const triggerDashboardTour = () => {
    const handleClose = async () => {
      await closeTourOrJumpToFinalStep({
        tour: dashboardTour,
        autoTriggered,
        onTourComplete: async () => {
          if (!tutorialCompletion['first_application']) {
            await markTutorialAsComplete({
              introducerId,
              tutorial: 'first_application',
            });
          }
        },
      });
    };

    const dragAndDropButtonElement = document.querySelector(
      '#status-column #drag-and-drop-button',
    );
    const filterIconElement = document.querySelector(
      '#termLength-column #column-filter-icon',
    );
    const sortIconElement = document.querySelector(
      '#approvalAmount-column #column-sort-icon',
    );
    const statusGuideElement = document.querySelector(
      '#status-column #status-guide',
    );

    const RED_DOT_CLASSES = {
      normal: ['highlight-red-dot'],
      central: ['highlight-red-dot', 'central-highlight'],
    } as const;

    const addRedDot = (
      element: Element | null,
      type: keyof typeof RED_DOT_CLASSES = 'normal',
    ) => element?.classList.add(...RED_DOT_CLASSES[type]);

    const removeRedDot = (
      element: Element | null,
      type: keyof typeof RED_DOT_CLASSES = 'normal',
    ) => element?.classList.remove(...RED_DOT_CLASSES[type]);

    const cleanupRedDots = () => {
      removeRedDot(filterIconElement);
      removeRedDot(sortIconElement);
      removeRedDot(dragAndDropButtonElement);
      removeRedDot(statusGuideElement, 'central');
    };

    const onCompleteTour = async () => {
      await handleClose();
      removeRedDot(statusGuideElement, 'central');
      trackClick('Dashboard', 'Got it');
      navigate(PATH_NAMES.home(), { replace: true });
    };

    const PROGRESS_STEP_COUNT = 7;

    const steps: DriveStep[] = [
      {
        element: '#quick-filters',
        popover: {
          showProgress: true,
          progressText: `1 of ${PROGRESS_STEP_COUNT}`,
          title: '⚡️ Quick filters',
          description: `<p>Move through the pipeline easily with grouped application statuses.</p>`,
          side: 'bottom',
          align: 'center',
          nextBtnText: 'Next',
          onNextClick: () => {
            dashboardTour.moveNext();
            trackClick('Dashboard', 'Next');
          },
        },
        disableActiveInteraction: true,
      },
      {
        element: '#search-bar',
        popover: {
          showProgress: true,
          progressText: `2 of ${PROGRESS_STEP_COUNT}`,
          popoverClass: 'driver-popover-custom',
          title: '🔍 Search bar',
          description: `<p>Find specific applications by entering a <strong>company name</strong>, <strong>company number</strong>, or <strong>applicant name</strong>.</p>`,
          nextBtnText: 'Next',
          side: 'bottom',
          align: 'center',
          onNextClick: () => {
            dashboardTour.moveNext();
            trackClick('Dashboard', 'Next');
          },
        },
        disableActiveInteraction: true,
      },
      {
        element: '#dashboard-tools',
        popover: {
          showProgress: true,
          progressText: `3 of ${PROGRESS_STEP_COUNT}`,
          popoverClass: 'driver-popover-custom',
          title: '⚙️ Dashboard tools',
          description: `<p>Export your application list as a CSV file and customise your dashboard with <strong>specific date range</strong> or <strong>columns</strong> to fit your workflow.</p>`,
          nextBtnText: 'Next',
          side: 'bottom',
          align: 'center',
          onNextClick: () => {
            dashboardTour.moveNext();
            trackClick('Dashboard', 'Next');
          },
        },
        disableActiveInteraction: true,
      },
      {
        element: '#termLength-column',
        onHighlightStarted: () => addRedDot(filterIconElement),
        popover: {
          showProgress: true,
          progressText: `4 of ${PROGRESS_STEP_COUNT}`,
          title: '🪄 Filter by column',
          description: `<p>Click the <strong>filter icon</strong> next to a column header to add criteria and refine your application list.</p>`,
          nextBtnText: 'Next',
          side: 'bottom',
          align: 'center',
          onNextClick: () => {
            dashboardTour.moveNext();
            removeRedDot(filterIconElement);
            trackClick('Dashboard', 'Next');
          },
        },
        disableActiveInteraction: true,
      },
      {
        element: '#approvalAmount-column',
        onHighlightStarted: () => addRedDot(sortIconElement),
        popover: {
          showProgress: true,
          progressText: `5 of ${PROGRESS_STEP_COUNT}`,
          title: '↕️ Sort by column',
          description: `<p>Click the <strong>sort icon</strong> next to a column header to sort your list in ascending or descending order.</p>`,
          nextBtnText: 'Next',
          side: 'bottom',
          align: 'center',
          onNextClick: () => {
            dashboardTour.moveNext();
            removeRedDot(sortIconElement);
            trackClick('Dashboard', 'Next');
          },
        },
        disableActiveInteraction: true,
      },
      {
        element: '#status-column',
        onHighlightStarted: () => addRedDot(dragAndDropButtonElement),
        popover: {
          showProgress: true,
          progressText: `6 of ${PROGRESS_STEP_COUNT}`,
          title: '✋ Drag & drop',
          description: `<p>Hover over a column header and drag it to rearrange columns and personalise your view.</p>`,
          nextBtnText: 'Next',
          side: 'bottom',
          align: 'center',
          onNextClick: () => {
            dashboardTour.moveNext();
            removeRedDot(dragAndDropButtonElement);
            trackClick('Dashboard', 'Next');
          },
        },
        disableActiveInteraction: true,
      },
      {
        element: '#status-column',
        onHighlightStarted: () => addRedDot(statusGuideElement, 'central'),
        popover: {
          showProgress: true,
          progressText: `7 of ${PROGRESS_STEP_COUNT}`,
          title: '📘 View status guide',
          description: `<p>Click <strong>“Application status”</strong> to open the status guide explaining what each status means.</p>`,
          nextBtnText: 'Got it',
          side: 'bottom',
          align: 'center',
          onNextClick: autoTriggered
            ? () => {
                dashboardTour.moveNext();
                trackClick('Dashboard', 'Got it');
              }
            : onCompleteTour,
        },
        disableActiveInteraction: true,
      },
    ];

    addRelaunchStepWhenApplicable(steps, autoTriggered, onCompleteTour);

    const dashboardTour = driver({
      showButtons: ['next', 'close'],
      popoverClass: 'driver-popover-custom',
      steps,
      overlayClickBehavior: undefined,
      onCloseClick: handleClose,
      // ensure red dots are removed when closing the tour at any step
      onDestroyStarted: cleanupRedDots,
    });

    dashboardTour.drive();
  };

  const tutorialCompletion = useMemo(() => {
    const defaultTutorialValues: Record<TTutorialName, boolean> = {
      new_user: true,
      calculator: true,
      first_application: true,
      existing_introducer_opt_in: true,
    };

    if (!tutorialsData?.data) return defaultTutorialValues;

    return tutorialsData.data.reduce((acc, tutorialData) => {
      acc[tutorialData.tutorial] = Boolean(tutorialData.completed_at);
      return acc;
    }, defaultTutorialValues);
  }, [tutorialsData]);

  const shouldShowTour = useMemo(
    () => ({
      newUser: !tutorialCompletion['new_user'],
      calculator: !tutorialCompletion['calculator'],
      firstApplication: !tutorialCompletion['first_application'],
      existingIntroducerOptIn:
        !tutorialCompletion['existing_introducer_opt_in'],
    }),
    [tutorialCompletion],
  );

  return {
    shouldShowTour,
    triggerNavigationTour,
    triggerDashboardTour,
    triggerCalculatorTour,
  };
};
